import React from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';
import FormikTextCopiable from '../../../components/formik-components/FormikTextCopiable';
import { LegacyInputTextComponent } from '../../../components/prime-formik';
import { validationMessages } from '../../../i18n/common';
import useSubscriptionExpiredRestrictions from '../../../hooks/useSubscriptionExpiredRestrictions';

const i18n = defineMessages({
  headerMessage: { id: 'del_header_assessment', defaultMessage: 'Delete assessment' },
  deleteConfirmWarning: { id: 'lbl.del_confirm.warning', defaultMessage: 'This action cannot be undone.' },
  confirmationCode: { id: 'lbl.confirmation_code', defaultMessage: 'Confirmation code' },
  confirmationCodeRetype: { id: 'lbl.confirmation_code_retype', defaultMessage: 'Please type the confirmation code' },
  deletionRequest: { id: 'lbl.deletion.request', defaultMessage: 'Anonymized 90 days after the request made on: ' },
});

const DialogFooter = ({ cancelDelete }) => {
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  return (
    <>
      <FormattedMessage id="actions.cancel" defaultMessage="Cancel">
        {(text) => <Button label={text} className="p-button-primary" type="button" onClick={cancelDelete} />}
      </FormattedMessage>
      &nbsp;
      <FormattedMessage id="actions.delete" defaultMessage="Delete ">
        {(text) => <Button label={text} className="p-button-danger" type="submit" disabled={subscriptionIsExpired} />}
      </FormattedMessage>
    </>
  );
};

DialogFooter.propTypes = {
  cancelDelete: PropTypes.func.isRequired,
};

const AssessmentDeleteConfirmForm = ({ cancelDelete, confirmDelete, isVisible, assessmentId }) => {
  const { formatMessage } = useIntl();

  const initialValues = {
    confirmation_code: assessmentId,
    re_confirmation_code: '',
  };

  const validationSchema = Yup.object().shape({
    confirmation_code: Yup.string().required(formatMessage(validationMessages.required)),
    re_confirmation_code: Yup.string()
      .oneOf([Yup.ref('confirmation_code'), null], validationMessages.sameAs)
      .required(formatMessage(validationMessages.required)),
    requestForDeletionDateChecked: Yup.boolean(),
    requestForDeletionOn: Yup.date().nullable(),
  });

  return (
    <Formik onSubmit={confirmDelete} initialValues={initialValues} validationSchema={validationSchema}>
      {({ handleSubmit }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <Dialog
              header={formatMessage(i18n.headerMessage)}
              footer={<DialogFooter cancelDelete={cancelDelete} />}
              visible={isVisible}
              onHide={cancelDelete}
              modal
            >
              <div className="panel panel-no-border">
                <div className="panel-body">
                  <div>
                    <div className="form-group">
                      <p>
                        <strong>{formatMessage(i18n.deleteConfirmWarning)}</strong>
                      </p>
                    </div>

                    <div className="form-group group-normalized">
                      <div>
                        <label>{formatMessage(i18n.confirmationCode)}</label>
                      </div>
                      <Field name="confirmation_code" keyValue={assessmentId} component={FormikTextCopiable} readOnly />

                      <div>
                        <label>{formatMessage(i18n.confirmationCodeRetype)}</label>
                      </div>
                      <Field name="re_confirmation_code" component={LegacyInputTextComponent} />
                    </div>
                  </div>
                </div>
              </div>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
};

AssessmentDeleteConfirmForm.propTypes = {
  cancelDelete: PropTypes.func.isRequired,
  confirmDelete: PropTypes.func.isRequired,
  isVisible: PropTypes.bool.isRequired,
  assessmentId: PropTypes.string.isRequired,
};

export default AssessmentDeleteConfirmForm;
